import React, { useEffect, useMemo, useState } from "react";
import API from "../../../api";
import {
  ArrowLeft,
  BadgeCheck,
  Briefcase,
  Building2,
  MapPin,
  Share2,
  Phone,
  Mail,
  MessageCircle,
  UserPlus,
  Users,
  Edit3,
  Star,
  Award
} from "lucide-react";

import AdvancedPortfolio from "./AdvancedPortfolio";
import "./ProfileProfessional.css";

export default function ProfileProfessional({
  user,
  posts = [],
  workPosts = [],
  loadingPosts = false,
  onDeleteWork,
  onAddPost = () => {},
  onPostWork = () => {},
  onEditProfile = () => {},
  onEditPost = () => {},
  onDeletePost = () => {},
  onConsult = () => {}
}){

  /* =====================================================
      LOGGED USER
  ===================================================== */

  const loggedInUser = useMemo(() => {
    try {
      return JSON.parse(
        localStorage.getItem("civilink_user")
      );
    } catch {
      return null;
    }
  }, []);

  

  /* =====================================================
      PROFILE OWNER
  ===================================================== */

  const isOwnProfile =
    loggedInUser?._id === user?._id;

  /* =====================================================
      STATE
  ===================================================== */

  const [connected, setConnected] =
    useState(false);

  const [imageLoaded, setImageLoaded] =
    useState(false);

  /* =====================================================
      CONNECTION STATUS
  ===================================================== */

  useEffect(() => {

    if (!loggedInUser || !user) return;

    setConnected(
      loggedInUser.connections?.includes(
        user._id
      )
    );

  }, [loggedInUser, user]);

  /* =====================================================
      CONNECT
  ===================================================== */

  const connectUser = async () => {
  try {
    const res = await API.post(
      `/connections/${user._id}/connect`
    );

    if (res.data.success) {
      setConnected(true);

      // Keep localStorage in sync
      if (loggedInUser) {
        const updatedUser = {
          ...loggedInUser,
          connections: [
            ...(loggedInUser.connections || []),
            user._id
          ]
        };

        localStorage.setItem(
          "civilink_user",
          JSON.stringify(updatedUser)
        );
      }
    }
  } catch (err) {
    console.error("Connect failed:", err);

    alert(
      err.response?.data?.message ||
      "Unable to connect."
    );
  }
};

  /* =====================================================
      SHARE
  ===================================================== */

  const profileUrl =
    `${window.location.origin}/profile/${user._id}`;

  const shareProfile = async () => {

    try {

      if (navigator.share) {

        await navigator.share({

          title:
            `${user.name} | Civilink`,

          text:
            `View ${user.name}'s Civilink profile`,

          url: profileUrl

        });

      } else {

        await navigator.clipboard.writeText(
          profileUrl
        );

        alert("Profile link copied");

      }

    } catch (err) {

      console.error(err);

    }

  };

  
  /* =====================================================
      PORTFOLIO
  ===================================================== */

  const canShowPortfolio =

    isOwnProfile ||

    !user.isProfessional ||

    user.professionalVerification?.status ===
      "approved";

  /* =====================================================
      PROFILE DATA
  ===================================================== */

  const verified =
    user.professionalVerification?.status ===
    "approved";

  const displayRole =
    user.profession ||
    user.role ||
    "Professional";

  const displayCompany =
    user.company ||
    user.organization ||
    "";

  const displayLocation = [

    user.location?.city,

    user.location?.state

  ]

    .filter(Boolean)

    .join(", ");

  const experience =

    user.experienceYears
      ? `${user.experienceYears} Years Experience`
      : "";

  const availableForWork =
  user.availabilityForWork;

  

  const recommendations =
    user.recommendationsCount || 0;

  const totalProjects =
    posts.length;

    const getRemainingTime = (expiresAt) => {
  if (!expiresAt) return "";

  const diff =
    new Date(expiresAt).getTime() - Date.now();

  if (diff <= 0) return "Expired";

  const days = Math.floor(
    diff / (1000 * 60 * 60 * 24)
  );

  const hours = Math.floor(
    (diff % (1000 * 60 * 60 * 24)) /
      (1000 * 60 * 60)
  );

  if (days > 0) {
    return `${days} day${days > 1 ? "s" : ""} left`;
  }

  return `${hours} hour${hours !== 1 ? "s" : ""} left`;
};

  /* =====================================================
      RETURN
  ===================================================== */

  return (
    <>

    
  {/* =====================================================
      COVER
  ===================================================== */}

  <div className="pro-cover">

    <div className="pro-cover-overlay" />

    <button
      className="pro-back-btn"
      onClick={() => window.history.back()}
    >
      <ArrowLeft size={20} />
    </button>

 {isOwnProfile && (
  <div className="pro-profile-actions">

    <button
      className="pro-edit-btn"
      onClick={onEditProfile}
    >
      <Edit3 size={18} />
      Edit Profile
    </button>

    <button
      className="pro-post-work-btn"
      onClick={onPostWork}
    >
      <Briefcase size={18} />
      Post a Work
    </button>

  </div>
)}

  </div>

  {/* =====================================================
      PROFILE CARD
  ===================================================== */}

  <div className="pro-profile-card">

    {/* ================= LEFT ================= */}

    <div className="pro-avatar-column">

      <div className="pro-avatar">

        {user.profilePhoto ? (

          <img
            src={user.profilePhoto}
            alt={user.name}
            className={
              imageLoaded
                ? "pro-avatar-img loaded"
                : "pro-avatar-img"
            }
            onLoad={() =>
              setImageLoaded(true)
            }
          />

        ) : (

          <div className="pro-avatar-placeholder">

            {user.name
              ?.charAt(0)
              ?.toUpperCase() || "U"}

          </div>

        )}

        {verified && (

          <div className="pro-verified">

            <BadgeCheck size={18} />

          </div>

        )}

      </div>

    </div>

    {/* ================= CENTER ================= */}

    <div className="pro-info-column">

      <div className="pro-name-row">

        <h1>

          {user.name || "Civilink Professional"}

        </h1>

        {verified && (

          <span className="verified-chip">

            <BadgeCheck size={16} />

            Verified

          </span>

        )}

      </div>

      <div className="pro-meta">

        <div className="pro-meta-item">

          <Briefcase size={16} />

          <span>

            {displayRole}

          </span>

        </div>

        {displayCompany && (

          <div className="pro-meta-item">

            <Building2 size={16} />

            <span>

              {displayCompany}

            </span>

          </div>

        )}

        {displayLocation && (

          <div className="pro-meta-item">

            <MapPin size={16} />

            <span>

              {displayLocation}

            </span>

          </div>

        )}

      </div>

      {experience && (

        <div className="experience-chip">

          <Award size={16} />

          {experience}

          
        </div>

      )}

      {availableForWork && (
  <div className="availability-chip">
    🟢 Available for Work
  </div>
)}

            {/* ================= ACTION BUTTONS ================= */}

      <div className="pro-action-grid">

        {!isOwnProfile ? (

          <>
            <button className="profile-btn primary">
              <MessageCircle size={18} />
              Consult
            </button>

            <button
              className={`profile-btn ${
                connected ? "secondary" : "primary"
              }`}
              disabled={connected}
              onClick={connectUser}
            >
              <UserPlus size={18} />
              {connected ? "Connected" : "Connect"}
            </button>
          </>

        ) : (

          <button
            className="profile-btn secondary"
            onClick={() =>
              window.location.assign(
                `/profile/${user._id}/connections`
              )
            }
          >
            <Users size={18} />
            Connections
          </button>

        )}

        <button
          className="profile-btn outline"
          onClick={shareProfile}
        >
          <Share2 size={18} />
          Share
        </button>

        {(user.phone || user.email) && (

          <button
            className="profile-btn outline"
            onClick={() => {

              if (user.phone) {

                window.location.href =
                  `tel:${user.phone}`;

              } else if (user.email) {

                window.location.href =
                  `mailto:${user.email}`;

              }

            }}
          >
            {user.phone ? (
              <Phone size={18} />
            ) : (
              <Mail size={18} />
            )}

            Contact
          </button>

        )}

      </div>

    </div>

    {/* ================= RIGHT ================= */}

    <div className="pro-stats-column">

      <div className="pro-stat-card">

        <div className="pro-stat-number">

          {totalProjects}

        </div>

        <div className="pro-stat-label">

          Projects

        </div>

      </div>

      <div className="pro-stat-card">

        <div className="pro-stat-number">

          {recommendations}

        </div>

        <div className="pro-stat-label">

          Recommendations

        </div>

      </div>

    </div>

  </div>

  {/* =====================================================
      BODY
  ===================================================== */}

  <div className="pro-body">
        {/* ================= LEFT COLUMN ================= */}

      <div className="pro-left-column">

        {/* =====================================
            ABOUT
        ===================================== */}

        {user.bio && (

          <section className="profile-card">

            <div className="profile-card-header">

              <h3>About</h3>

            </div>

            <div className="profile-about">

              {user.bio}

            </div>

          </section>

        )}

        {/* =====================================
            PROFESSIONAL INFORMATION
        ===================================== */}

        <section className="profile-card">

          <div className="profile-card-header">

            <h3>Professional Information</h3>

          </div>

          <div className="professional-info-grid">

            {displayRole && (

              <div className="info-card">

                <Briefcase
                  size={20}
                  className="info-icon"
                />

                <div>

                  <small>
                    Profession
                  </small>

                  <strong>

                    {displayRole}

                  </strong>

                </div>

              </div>

            )}

            {displayCompany && (

              <div className="info-card">

                <Building2
                  size={20}
                  className="info-icon"
                />

                <div>

                  <small>
                    Company
                  </small>

                  <strong>

                    {displayCompany}

                  </strong>

                </div>

              </div>

            )}

            {displayLocation && (

              <div className="info-card">

                <MapPin
                  size={20}
                  className="info-icon"
                />

                <div>

                  <small>
                    Location
                  </small>

                  <strong>

                    {displayLocation}

                  </strong>

                </div>

              </div>

            )}

            {experience && (

              <div className="info-card">

                <Award
                  size={20}
                  className="info-icon"
                />

                <div>

                  <small>
                    Experience
                  </small>

                  <strong>

                    {experience}

                  </strong>

                </div>

              </div>

            )}

          </div>

        </section>

        {/* =====================================
            SKILLS
        ===================================== */}

        {Array.isArray(user.skills) &&
          user.skills.length > 0 && (

          <section className="profile-card">

            <div className="profile-card-header">

              <h3>Skills</h3>

            </div>

            <div className="skills-grid">

              {user.skills.map((skill) => (

                <div
                  key={skill}
                  className="skill-chip"
                >

                  <Star size={14} />

                  <span>

                    {skill}

                  </span>

                </div>

              ))}

            </div>

          </section>

        )}

      </div>
            {/* ================= RIGHT COLUMN ================= */}

      <div className="pro-right-column">

        {/* =====================================
            CONTACT
        ===================================== */}

        {(user.phone || user.email) && (

          <section className="profile-card">

            <div className="profile-card-header">

              <h3>Contact</h3>

            </div>

            <div className="contact-grid">

              {user.phone && (

                <a
                  href={`tel:${user.phone}`}
                  className="contact-card primary"
                >

                  <Phone size={22} />

                  <div>

                    <small>Phone</small>

                    <strong>

                      {user.phone}

                    </strong>

                  </div>

                </a>

              )}

              {user.email && (

                <a
                  href={`mailto:${user.email}`}
                  className="contact-card"
                >

                  <Mail size={22} />

                  <div>

                    <small>Email</small>

                    <strong>

                      {user.email}

                    </strong>

                  </div>

                </a>

              )}

            </div>

          </section>

        )}

        {/* =====================================
    WORK POSTS
===================================== */}

{workPosts.length > 0 && (
  <section className="profile-card work-posts-card">

    <div className="profile-card-header">
      <div>
        <h3>Work Posts</h3>
        <p className="work-posts-subtitle">
          Current work opportunities
        </p>
      </div>

      <Briefcase size={20} />
    </div>

    <div className="work-posts-list">

      {workPosts.map((work) => (
        <div
          key={work._id}
          className="work-post-item"
        >

          <div className="work-post-content">

            <h4>{work.title}</h4>

            <p>{work.description}</p>

            <div className="work-post-meta">

              {work.location?.city && (
                <span>
                  <MapPin size={14} />
                  {work.location.city}
                  {work.location.state
                    ? `, ${work.location.state}`
                    : ""}
                </span>
              )}

              {work.workType && (
                <span>
                  {work.workType}
                </span>
              )}

              {work.budget > 0 && (
                <span>
                  ₹{Number(work.budget).toLocaleString("en-IN")}
                  {work.budgetType
                    ? ` • ${work.budgetType}`
                    : ""}
                </span>
              )}

              <span className="work-post-expiry">
  {getRemainingTime(work.expiresAt)}
</span>

            </div>

            {work.skills?.length > 0 && (
              <div className="work-post-skills">
                {work.skills.map((skill, index) => (
                  <span key={index}>
                    {skill}
                  </span>
                ))}
              </div>
            )}

          </div>

          {!isOwnProfile && work.postedBy && (
            <button
              className="profile-btn primary"
              onClick={() => onConsult(work.postedBy)}
            >
              <MessageCircle size={17} />
              Consult
            </button>
          )}

          {isOwnProfile && (
  <button
    className="work-post-delete-btn"
    onClick={() => onDeleteWork(work._id)}
  >
    Delete
  </button>
)}

        </div>
      ))}

    </div>

  </section>
)}

        {/* =====================================
            PORTFOLIO
        ===================================== */}

        <section className="profile-card">

          <div className="profile-card-header">

            <h3>Portfolio</h3>

            {isOwnProfile && (

              <button
                className="portfolio-add-btn"
                onClick={onAddPost}
              >
                Add Project
              </button>

            )}

          </div>

          {canShowPortfolio ? (

            <AdvancedPortfolio
    posts={posts}
    loading={loadingPosts}
    isOwnProfile={isOwnProfile}
    onAddPost={onAddPost}
    onEditPost={onEditPost}
    onDeletePost={onDeletePost}
/>

          ) : (

            <div className="portfolio-hidden">

              <BadgeCheck size={24} />

              <h4>

                Portfolio Locked

              </h4>

              <p>

                Your portfolio will become visible
                after your professional verification
                is approved by Civilink.

              </p>

            </div>

          )}

        </section>

      </div>

    </div>

  </>

);

}


